/**
 * `X-Control-Token` — 제어락 리스 토큰의 보관과 자동 첨부.
 *
 * 🔴 **이 축은 정본 yaml 에 0건이고 실물 스펙에만 14곳 선언돼 있다**(2026-09-17 실측 ·
 *    `docs/api-survey.md` 4.3). 정본에서 생성한 타입만 보면 이 헤더가 통째로 빠지므로
 *    **경로표를 손으로 들고 있는다.** 계약이 헤더를 선언하면 이 파일을 지우고 생성 타입을 따른다.
 *    → 백엔드 보고 대상 D.
 *
 * ⚠ 실물 스펙의 `required` 는 `POST /control-token/heartbeat` 만 `true` 지만, **런타임 거부는 별개**다.
 *    live 설명문이 「토큰이 현재 유효 토큰일 때만 — 아니면 409」라고 적는다. 그래서 표에 있는
 *    경로에는 **토큰이 있으면 항상 붙인다.** 없으면 붙이지 않고 서버가 거부하게 둔다(상태 추론 금지).
 */

import type { OrchestratorSchemas } from '@ctcells/contract'
import {
  AUTH_STORAGE_KEYS,
  CONTROL_LOCK_TTL_FALLBACK_MS,
  safeStorage,
  useSessionStore,
} from '@ctcells/core'

/** 경로 패턴 — `{}` 자리는 아무 세그먼트나 받는다. base(`/api/v1`) 를 뺀 계약 경로다 */
interface ControlTokenRoute {
  /** ⚠ `GET` 은 `/scheduler` 하나다 — 헤더가 선택이고, 실리면 `reset_allowed` 가 «이 요청자가 쥐었나»를 본다 */
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /** 경로 패턴. `{id}` 는 한 세그먼트 와일드카드다 */
  pattern: string
}

/** 실물 스펙이 `X-Control-Token` 을 선언한 operation 중 «프론트가 부르는 것». 전수는 2026-09-28 기준 26건이다(2026-09-29 스케줄러 · 수동 이송 · 복구 개시 · 파지 기억 초기화를 더했다) */
export const CONTROL_TOKEN_ROUTES: readonly ControlTokenRoute[] = [
  { method: 'POST', pattern: '/control-token/heartbeat' },
  { method: 'DELETE', pattern: '/control-token' },
  { method: 'POST', pattern: '/batches' },
  { method: 'POST', pattern: '/batches/{id}/start' },
  { method: 'POST', pattern: '/batches/{id}/stop' },
  { method: 'POST', pattern: '/batches/{id}/pause' },
  { method: 'POST', pattern: '/batches/{id}/resume' },
  { method: 'POST', pattern: '/batches/{id}/recover' },
  /*
   * 🔴 **배치 준비 강제 정리**(2026-09-22 에 더했다). `views/modules.py:1479` 가
   *    `require_control_lock` 을 건다 — 여기 없으면 헤더 없이 나가 **409** 다.
   * ⚠ 마디 수가 3 이라 `/modules/{id}/…`(4마디) 와 겹치지 않는다(`matches()` 는 길이부터 본다).
   */
  { method: 'POST', pattern: '/modules/prepare' },
  { method: 'PUT', pattern: '/modules/{id}/control-owner' },
  { method: 'POST', pattern: '/modules/{id}/recovery/reset' },
  /*
   * 🔴 **실행 취소 중계**(2026-09-28 더함 · `views/module_control.py` `require_control_lock`).
   *    작업 「중지」 뒤 모듈 실행을 멈추는 길이다(`commands/moduleExecution.ts`). 여기 없으면 헤더 없이 나가 409 다.
   * ⚠ 실서버가 헤더를 선언한 `/modules/{id}/control/*` 는 이 밖에도 일곱이 더 있다(shutdown ·
   *   pause · resume · diagnostics · load-confirm · unload-confirm — `sequences` 는 아래에 올렸다). 부르는 화면이 없어서 안 올렸다 —
   *   붙이는 날 같이 올린다.
   */
  { method: 'POST', pattern: '/modules/{id}/control/executions/{id}/cancel' },
  /*
   * 🔴 **초기화 중계**(2026-09-28 더함 · 같은 파일 `_plain(mc.INITIALIZE)` · `require_control_lock`).
   *    모듈 서랍의 「초기화」가 부른다(`commands/moduleControl.ts`).
   */
  { method: 'POST', pattern: '/modules/{id}/control/initialize' },
  /*
   * 🔴 **진단 중계**(2026-10-22 더함 · 같은 파일 `_plain(mc.DIAGNOSTICS)` · `require_control_lock`).
   *    모듈 서랍 「제어」 카드의 「진단」이 부른다(`commands/moduleControl.ts`). 없으면 409 `no_token_header`(로컬 실측).
   */
  { method: 'POST', pattern: '/modules/{id}/control/diagnostics' },
  /*
   * 🔴 **시퀀스 중계**(2026-09-28 더함 · 같은 파일 `run_module_sequence_control` · `require_control_lock`).
   *    개발용 「테스트 작업 시작」의 자동 준비가 팔 복귀 · 도어 닫기를 보낸다(`commands/moduleControl.ts`).
   */
  { method: 'POST', pattern: '/modules/{id}/control/sequences' },
  /*
   * 🔴 **복구 개시**(2026-09-29 더함 · `views/modules.py` `start_module_recovery` · `require_control_lock`).
   *    `ERROR` 모듈은 `/initialize` 를 `MODULE_IN_ERROR` 로 거절한다 — 출구가 이것이다(`commands/moduleRecovery.ts`).
   */
  { method: 'POST', pattern: '/modules/{id}/recovery/start' },
  /*
   * 🔴 **파지 기억 초기화**(2026-09-29 더함 · `views/modules.py` `forget_module_grips` · `require_control_lock`).
   *    작업 중 모듈이 재기동돼 실행 없이 「쥐고 있다」만 남으면 초기화가 `robot_holding_tray` 로 막힌다 — 출구가 이것이다.
   */
  { method: 'POST', pattern: '/modules/{id}/grips/forget' },
  /*
   * 🔴 **자리 비움 확약**(2026-10-18 더함 · v0.9.4 `views/module_control.py` handoff-release · `IFC-RO-54` · 제어락).
   *    개발용 「테스트 작업 시작」이 장부에 행이 없는(=모름) 핸드오프 자리를 비었다고 확약한다(`commands/moduleRecovery.ts`).
   */
  { method: 'POST', pattern: '/modules/{id}/control/handoff-release' },
  /*
   * 🔴 **조그 · 지점 티칭 · 슬라이드 mm 티칭**(2026-10-12 더함 · `views/modules.py` `jog_module` · `teach_module_position` ·
   *    `teach_module_slide_position` — 전부 `require_control_lock`). 모듈 서랍 「위치」 탭이 부른다(`commands/positions.ts`).
   * ⚠ 같은 경로의 GET(`/modules/{id}/positions` · `/pose`)은 헤더가 없다 — 메서드로 갈린다.
   */
  { method: 'POST', pattern: '/modules/{id}/jog' },
  { method: 'PUT', pattern: '/modules/{id}/positions/{id}' },
  { method: 'PUT', pattern: '/modules/{id}/positions/{id}/slide-mm' },
  /*
   * 🔴 **설치 정의 쓰기**(2026-10-23 더함 · `views/modules.py` `set_module_installed_labware` · `require_control_lock`).
   *    작업 줄 시작 창의 점검 조치(`RCP-LBW-09`)가 부른다(`commands/positions.ts` `useSetInstalledLabware`).
   */
  { method: 'PUT', pattern: '/modules/{id}/positions/{id}/installed-labware' },
  /*
   * 🔴 **수동 이송 시작**(2026-09-29 더함 · `views/handoff.py` `start_manual_transfer` · `require_control_lock`).
   *    HANDOFF 에 쥔 채 남은 6축의 트레이를 놓는다(`commands/manualTransfer.ts`).
   */
  { method: 'POST', pattern: '/transfers/manual' },
  /*
   * 🔴 **스케줄러**(2026-09-29 더함 · `views/scheduler.py`). 해제는 `require_control_lock` 이고, 조회는 헤더가
   *    선택이지만 실려야 `reset_allowed` 가 참이 될 수 있다(서버가 «이 요청자가 쥐었나»를 본다).
   */
  { method: 'GET', pattern: '/scheduler' },
  { method: 'POST', pattern: '/scheduler/reset' },
  { method: 'PUT', pattern: '/recipes/{id}' },
  /*
   * 🔴 **한 번에 풀기**(2026-10-11 더함 · v0.9.4 `views/stops.py` `release_stops` · `require_control_lock`).
   *    종전 `/resource-locks/{id}/release`(자원 점유 해제)를 서버가 걷고 이 창구로 모았다.
   */
  { method: 'POST', pattern: '/safety/stops/release' },
  { method: 'POST', pattern: '/safety/clear' },
  { method: 'PUT', pattern: '/system/mode' },
]

function matches(pattern: string, path: string): boolean {
  const p = pattern.split('/')
  const q = path.split('/')
  if (p.length !== q.length) return false
  return p.every((seg, i) =>
    seg.startsWith('{') && seg.endsWith('}') ? q[i] !== '' : seg === q[i],
  )
}

export function requiresControlToken(method: string, path: string): boolean {
  const upper = method.toUpperCase()
  return CONTROL_TOKEN_ROUTES.some(
    (route) => route.method === upper && matches(route.pattern, path),
  )
}

/* ───────────────────────── 리스 보관소 ─────────────────────────────────── */

/**
 * 🔴 **서버가 「만료된 내 락」과 「남이 쥔 락」을 같은 409 `CONTROL_LOCK_HELD` 로 답한다.**
 *    가드가 붙은 경로(배치 명령 · 안전 해제 · 모듈 · 시스템 모드)는 `context.reason` 으로
 *    셋을 갈라 주지만(`app/core/control_guard.py` — `no_token_header`·`lease_expired`·
 *    `held_by_other`), **`/control-token` 획득·갱신은 그 문맥을 싣지 않는다**
 *    (`views/control_token.py` `_held()` — `context` 없음, 실측 2026-09-20).
 *    그래서 그 두 경로는 **「내가 방금까지 쥐고 있었나」**로 가른다. 그 기억이 이 보관소다.
 */
export const CONTROL_LOCK_DENIAL_REASONS = [
  'no_token_header',
  'lease_expired',
  'held_by_other',
] as const

/** 서버 `ErrorResponse.context.reason` 어휘 그대로. 프론트가 만든 말이 아니다 */
export type ControlLockDenialReason = (typeof CONTROL_LOCK_DENIAL_REASONS)[number]

export type ControlLockStatus =
  | 'unknown'
  /** 아직 안 쥐었거나 반납했다 */
  | 'none'
  /** 내가 쥐고 있다(리스가 살아 있다) */
  | 'mine'
  /** 내 리스가 만료·회수됐다 — 다시 획득해야 한다. 기다린다고 풀리지 않는다 */
  | 'expired'
  /** 남이 쥐고 있다 — 반납을 기다리거나 관리자가 해제한다 */
  | 'other'

export interface ControlLease {
  sessionId?: string | null
  token: string
  /** 서버가 준 보유자 표기. 없으면 없는 대로 둔다(지어내지 않는다) */
  holder: string | undefined
  acquiredAt: string | undefined
  expiresAt: string | undefined
  /** 만료 시각(epoch ms). 계산할 수 없으면 획득 시점 + 폴백 TTL */
  expiresAtMs: number
  /**
   * 서버 시계 − 이 기기 시계(ms). **획득 때 한 번** 잰다(획득 응답의 `acquired_at` 은 그 순간의 서버 시각이다).
   * 갱신 응답의 만료 시각을 이 기기 시계로 옮길 때 쓴다. 못 쟀으면 없다(그때는 서버 시각을 그대로 믿는다).
   */
  skewMs?: number | undefined
  /** 리스 길이(ms) — 획득 응답의 「만료 − 획득」. 갱신 응답이 이보다 길게 읽히지 않게 막는다(아래 `setControlLease`) */
  ttlMs?: number | undefined
  /** 이 리스를 받은 이 기기 시각(epoch ms) — 남은 시간 표시가 «옛 지금»으로 빼지 않게 한다(`ControlLockChip`) */
  receivedAtMs?: number | undefined
}

export interface ControlLockSnapshot {
  displayName?: string | undefined
  status: ControlLockStatus
  /** `mine` 일 때만 있다. 토큰 값은 여기 말고 어디에도 흘리지 않는다 */
  lease: ControlLease | null
  /** 서버가 준 보유자 표기. `other` 에서 서버가 문맥을 줬을 때만 채워진다 */
  holder: string | undefined
  /** 서버가 준 만료 시각(ISO). 카운트다운은 프론트가 만들지 않는다 */
  expiresAt: string | undefined
}

/**
 * 현재 보유한 리스.
 *
 * 🔴 **메모리에만 둔다.** 리스는 TTL 이 짧고 서버가 회수한다 — 저장소에 남기면
 *    죽은 토큰으로 요청을 보내게 된다. 세션이 끊기면 서버가 락도 정리한다(서버 SSOT).
 *    프론트가 `DELETE /control-token` 을 자동으로 쏘지 않는다 — 그건 상태 추론이다.
 * 🔴 **공용 단말이라 세션과 같은 원칙이다** — 탭을 닫으면 사라진다.
 */
const NO_LOCK: ControlLockSnapshot = {
  status: 'none',
  lease: null,
  holder: undefined,
  expiresAt: undefined,
}

/**
 * 리스를 **탭 저장소**(`sessionStorage`)에도 둔다 — 새로고침이 리스를 잃지 않게.
 *
 * 🔴 **실물 사고**(히나 · 2026-09-22): 페이지를 새로고침하면 보관소가 메모리라 비고, 서버 락은 이 세션이 그대로 쥐고
 *    있어 다시 획득하면 409 `CONTROL_LOCK_HELD` — 칩이 「다른 사용자가 보유」로 서서 **같은 사람인데 빠져나올 문이
 *    없었다**(리스가 만료되는 30초까지). 토큰을 탭 저장소에 두면 새로고침 뒤 그 리스를 그대로 이어 쓰고 심장박동도 이어진다.
 * ⚠ 탭 저장소라 다른 탭·다른 브라우저로는 안 새고, 탭을 닫으면 사라진다(그때는 리스가 만료로 풀린다).
 * ⚠ 만료 시각(`expiresAtMs`)이 지난 저장분은 되살리지 않는다 — 만료 토큰으로 조작하지 않는 규율 그대로.
 */
export const CONTROL_LEASE_STORAGE_KEY = 'ctcells:control-lease'

function persist(next: ControlLockSnapshot): void {
  if (next.status === 'mine' && next.lease !== null) {
    safeStorage.writeJson(CONTROL_LEASE_STORAGE_KEY, next.lease, 'session')
  } else {
    safeStorage.remove(CONTROL_LEASE_STORAGE_KEY, 'session')
  }
}

/** 탭 저장소의 리스를 되살린다(모듈이 처음 설 때). 없거나 만료면 `none` 이다 */
export function restoreControlLease(now: number = Date.now()): ControlLockSnapshot {
  const stored = safeStorage.readJson<ControlLease>(CONTROL_LEASE_STORAGE_KEY, 'session')
  if (
    stored === null ||
    typeof stored.token !== 'string' ||
    (stored.sessionId ?? null) !== safeStorage.read(AUTH_STORAGE_KEYS.session, 'session') ||
    typeof stored.expiresAtMs !== 'number' ||
    stored.expiresAtMs <= now
  ) {
    safeStorage.remove(CONTROL_LEASE_STORAGE_KEY, 'session')
    return NO_LOCK
  }
  return { status: 'mine', lease: stored, holder: stored.holder, expiresAt: stored.expiresAt }
}

let snapshot: ControlLockSnapshot = { ...restoreControlLease(), status: 'unknown' }
let revision = 0
let eventTime = -Infinity
let remoteState: OrchestratorSchemas['schemas']['ControlLockState'] | null = null
let lockSessionId = safeStorage.read(AUTH_STORAGE_KEYS.session, 'session')

const listeners = new Set<() => void>()

function publish(next: ControlLockSnapshot): void {
  revision += 1
  snapshot = next
  persist(next)
  for (const listener of listeners) listener()
}

/** `useSyncExternalStore` 용. 반환 객체는 **바뀔 때만** 새로 만든다 */
export function getControlLockSnapshot(): ControlLockSnapshot {
  return snapshot
}

export function subscribeControlLock(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * **제어락 거절(409 `CONTROL_LOCK_HELD`)만** 듣는 축. 🔴 위 `subscribeControlLock` 과 가른다.
 *
 * 저쪽은 **상태가 바뀔 때마다** 울려서 획득·반납·하트비트까지 탄다. 캐시 무효화를 거기 걸면
 * 성공 경로가 **두 번**(명령 훅의 `invalidates` + 여기) 긁고, 하트비트가 상태를 다시 실을
 * 때마다 긁는다 — 그것이 곧 주기 폴링이다(DP-01). 이 축은 **거절 한 번에 한 번**만 운다.
 */
const denialListeners = new Set<() => void>()

export function subscribeControlLockDenied(listener: () => void): () => void {
  denialListeners.add(listener)
  return () => {
    denialListeners.delete(listener)
  }
}

/** 획득 응답의 리스 길이(만료 − 획득). 못 읽거나 0 이하면 `undefined` */
function leaseTtlMs(lease: { acquired_at?: string; expires_at?: string }): number | undefined {
  const acquired = lease.acquired_at === undefined ? NaN : Date.parse(lease.acquired_at)
  const expires = lease.expires_at === undefined ? NaN : Date.parse(lease.expires_at)
  return Number.isNaN(acquired) || Number.isNaN(expires) || expires <= acquired
    ? undefined
    : expires - acquired
}

/** 획득 응답으로 서버 시계 − 이 기기 시계를 잰다. 못 읽으면 `undefined` */
export function leaseSkewMs(acquiredAt: string | undefined, now: number): number | undefined {
  const acquired = acquiredAt === undefined ? NaN : Date.parse(acquiredAt)
  return Number.isNaN(acquired) ? undefined : acquired - now
}

/**
 * 서버가 준 만료 시각을 **이 기기 시계로** 옮긴다. 못 읽으면 폴백 TTL(정본 인용)이다.
 *
 * 🔴 **서버 시계와 이 기기 시계가 다를 수 있다** — 잰 차이(`skewMs`)만큼 빼서 옮긴다.
 * 🔴 **「만료 − 획득」을 수명으로 쓰지 않는다**(2026-10-14 실제 버그 · eric 「30초에서.. 40초가 되고.. 50초가 되면서
 *    늘어나는데 왜 그래?」). 서버는 갱신 때 `acquired_at` 을 **처음 획득 시각 그대로** 두고 `expires_at` 만 「지금 + TTL」로
 *    민다(`control_lock.heartbeat`). 그래서 그 차이는 갱신마다 TTL + 보유 시간으로 커졌고, 남은 시간이 30 → 40 → 50초로
 *    늘었다. 그 값으로 갱신 간격(수명의 1/3)을 잡아 간격도 같이 늘었고, 1분여 뒤에는 간격이 서버 TTL(30초)을 넘어 락이 풀렸다.
 */
export function leaseExpiryMs(
  expiresAt: string | undefined,
  skewMs: number | undefined,
  now: number,
): number {
  const expires = expiresAt === undefined ? NaN : Date.parse(expiresAt)
  if (Number.isNaN(expires)) return now + CONTROL_LOCK_TTL_FALLBACK_MS
  return skewMs === undefined ? expires : expires - skewMs
}

/**
 * 획득·갱신 성공 — 서버가 준 리스 그대로.
 * 시계 차이는 **획득 때만** 잰다 — 같은 토큰의 갱신은 앞서 잰 값을 잇는다(갱신 응답의 `acquired_at` 은 옛 시각이다).
 */
export function setControlLease(
  lease: { control_token: string; holder?: string; acquired_at?: string; expires_at?: string },
  now: number = Date.now(),
  origin: 'acquire' | 'heartbeat' = 'acquire',
): void {
  const previous = snapshot.lease?.token === lease.control_token ? snapshot.lease : null
  if (origin === 'heartbeat' && (previous === null || snapshot.status !== 'mine')) return
  const skewMs = origin === 'heartbeat' ? previous?.skewMs : leaseSkewMs(lease.acquired_at, now)
  const ttlMs = origin === 'heartbeat' ? previous?.ttlMs : leaseTtlMs(lease)
  /*
   * 🔴 갱신 직후 남은 시간은 리스 길이를 넘지 않는다(eric 2026-10-14 「20초에서 10초 더해서 30이 되었으면 좋겠는데.. 31이 돼」).
   *    시계 차이는 획득 응답이 오간 시간만큼 어긋나 잰다 — 그 몇십 ms 가 올림 표시에서 1초로 보였다.
   */
  const raw = leaseExpiryMs(lease.expires_at, skewMs, now)
  const expiresAtMs = ttlMs === undefined ? raw : Math.min(raw, now + ttlMs)
  publish({
    status: 'mine',
    lease: {
      sessionId: useSessionStore.getState().sessionId,
      token: lease.control_token,
      holder: lease.holder,
      acquiredAt: lease.acquired_at,
      expiresAt: lease.expires_at,
      expiresAtMs,
      receivedAtMs: now,
      ...(skewMs === undefined ? {} : { skewMs }),
      ...(ttlMs === undefined ? {} : { ttlMs }),
    },
    holder: lease.holder,
    displayName: snapshot.displayName,
    expiresAt: lease.expires_at,
  })
}

/** 반납 성공 · 로그아웃 — 아무것도 모르는 상태로 되돌린다 */
export function clearControlLock(): void {
  eventTime = -Infinity
  remoteState = null
  publish(NO_LOCK)
}

export function controlLockRevision(): number {
  return revision
}

export function markControlLockUnknown(): void {
  remoteState = null
  publish({ ...NO_LOCK, status: 'unknown' })
}

export function suspendControlLock(): void {
  remoteState = null
  publish({ ...snapshot, status: 'unknown' })
}

export function applyControlLockState(
  value: unknown,
  expectedRevision?: number,
  ts?: string,
): boolean {
  if (expectedRevision !== undefined && expectedRevision !== revision) return false
  const at = ts === undefined ? undefined : Date.parse(ts)
  if (at !== undefined && (!Number.isFinite(at) || at < eventTime)) return false
  if (at !== undefined) eventTime = at
  if (
    typeof value !== 'object' ||
    value === null ||
    !('held' in value) ||
    typeof value.held !== 'boolean' ||
    !('mine' in value) ||
    typeof value.mine !== 'boolean' ||
    !('holder' in value) ||
    (value.mine && !value.held)
  ) {
    markControlLockUnknown()
    return true
  }
  const state = value as OrchestratorSchemas['schemas']['ControlLockState']
  if (
    state.holder !== null &&
    (typeof state.holder !== 'object' ||
      typeof state.holder.display_name !== 'string' ||
      typeof state.holder.user_id !== 'string')
  ) {
    markControlLockUnknown()
    return true
  }
  remoteState = state
  const lease =
    state.mine && snapshot.lease?.acquiredAt === state.acquired_at ? snapshot.lease : null
  /*
   * 🔴 **내 락이 풀렸다는 사건이면 «만료»다**(히나 조사 1-2 · 2026-10-23) — 내가 쥐고 있었는데(`mine`) 서버가 `held: false` 를 말하면
   *    강제 회수 · 리스 만료다. 「없음」으로 접으면 「아직 안 잡았다」와 같은 얼굴이 되어 왜 단추가 다 꺼졌는지 모른다(`ControlLockChip` 「다시 획득」).
   *    ⚠ 내가 반납한 경우는 반납 응답이 `clearControlLock()` 으로 «없음»을 먼저 세운다(`commands/controlLock.ts`).
   */
  const wasMine = snapshot.status === 'mine'
  publish({
    status: !state.held
      ? wasMine
        ? 'expired'
        : 'none'
      : !state.mine
        ? 'other'
        : lease === null
          ? 'expired'
          : 'mine',
    lease,
    holder: state.holder?.user_id,
    displayName: state.holder?.display_name,
    expiresAt: state.expires_at ?? undefined,
  })
  return true
}

export function canApplyAcquiredLease(
  startRevision: number,
  sessionId: string | null,
  acquiredAt: string,
): boolean {
  return (
    sessionId === useSessionStore.getState().sessionId &&
    (startRevision === revision ||
      (remoteState?.mine === true && remoteState.acquired_at === acquiredAt))
  )
}

useSessionStore.subscribe((state) => {
  if (state.status === 'unknown') return
  if (state.sessionId === lockSessionId && state.status === 'authenticated') return
  lockSessionId = state.sessionId
  eventTime = -Infinity
  remoteState = null
  markControlLockUnknown()
})

/**
 * 서버가 409 `CONTROL_LOCK_HELD` 로 거절했다.
 *
 * 🔴 **낙관적으로 「아직 내 것」이라 믿지 않는다** — 거절을 받은 순간 보유 상태를 내려놓는다.
 * @param reason 가드가 준 `context.reason`. 획득·갱신 경로는 주지 않으므로 `undefined` 가 온다
 */
export function markControlLockDenied(denial: {
  reason?: string | undefined
  holder?: string | undefined
  expiresAt?: string | undefined
}): void {
  remoteState = null
  const held = snapshot.status === 'mine'
  const status: ControlLockStatus =
    denial.reason === 'held_by_other'
      ? 'other'
      : denial.reason === 'lease_expired'
        ? 'expired'
        : denial.reason === 'no_token_header'
          ? 'unknown'
          : // 🔴 서버가 문맥을 안 준 자리(`/control-token` 획득·갱신). **내가 쥐고 있었으면
            //    만료이고, 아니면 남이 쥔 것**이다 — 그 둘이 다음 행동이 정반대라 갈라야 한다.
            held
            ? 'expired'
            : 'other'
  publish({
    status,
    lease: null,
    holder: denial.holder,
    expiresAt: denial.expiresAt,
  })
  // 🔴 **거절은 「내 캐시가 틀렸다」는 통지다.** 락이 남에게 가 있으면 서버가 주는
  //    `allowed_commands` 가 전부 빈 배열인데, 그 변화를 알려 주는 구독 이벤트가 없다(BR-55).
  //    듣는 쪽은 `stream/bridge.ts` 다 — 이 파일은 쿼리 캐시를 모른다.
  for (const listener of denialListeners) listener()
}

/** 리스 수명이 다했다 — 타이머가 부른다. 서버에 묻지 않고도 버튼을 먼저 닫는 자리다 */
export function expireControlLockIfDue(now: number = Date.now()): void {
  if (snapshot.status !== 'mine' || snapshot.lease === null) return
  if (snapshot.lease.expiresAtMs > now) return
  publish({ status: 'expired', lease: null, holder: undefined, expiresAt: undefined })
}

/**
 * 저수준 설정. **화면·훅이 부르지 않는다** — 시험과 하위호환용이다.
 * 리스 시각을 모르는 채로 토큰만 넣으면 폴백 TTL 로 동작한다.
 */
export function setControlToken(token: string | null): void {
  if (token === null) clearControlLock()
  else setControlLease({ control_token: token })
}

export function getControlToken(): string | null {
  return snapshot.status === 'mine' ? (snapshot.lease?.token ?? null) : null
}
